import {
  POST_RECORDING_RESUME_WINDOW_MS,
  SIX_MINUTE_WALK_BASE_PATH,
} from "./sixMinuteWalkConfig";

export function getStatusValue(event) {
  if (typeof event === "string") return event;
  if (event && typeof event === "object" && typeof event.status === "string") {
    return event.status;
  }
  return null;
}

export function getUploadId(event) {
  if (!event || typeof event !== "object") return null;

  const candidate =
    event.uploadId ?? event.recordingId ?? event.docid ?? event.docId ?? null;

  if (candidate == null) return null;
  const normalized = String(candidate).trim();
  return normalized || null;
}

function createAssessmentId() {
  const iso = new Date().toISOString().replace(/:/g, "-");
  const rand = Math.random().toString(36).slice(2, 8);

  return `6mwt-${iso}-${rand}`;
}

export function createAssessmentState(overrides = {}) {
  return {
    assessmentId: createAssessmentId(),
    preMeasurementId: null,
    walk: null,
    postResume: null,
    returnedAt: null,
    postRecordingStartedAt: null,
    recoveryDelayMs: null,
    postMeasurementId: null,
    ...overrides,
  };
}

export function parseTimestampMs(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  const timestampMs = Date.parse(value);
  return Number.isFinite(timestampMs) ? timestampMs : null;
}

export function buildPostRecordingResumeState({
  completedAt,
  trackingStoppedAt,
  returnedAt,
  continuedAt = null,
}) {
  const resumeStartedAtMs =
    parseTimestampMs(trackingStoppedAt) ?? parseTimestampMs(completedAt);

  if (!Number.isFinite(resumeStartedAtMs)) {
    return null;
  }

  return {
    startedAt: new Date(resumeStartedAtMs).toISOString(),
    expiresAt: new Date(
      resumeStartedAtMs + POST_RECORDING_RESUME_WINDOW_MS,
    ).toISOString(),
    timeToRecordingStartedAt:
      returnedAt || trackingStoppedAt || completedAt || null,
    continuedAt: continuedAt || null,
  };
}

export function hasPostRecordingContinueIntent(assessment) {
  return Boolean(
    assessment?.postRecordingStartedAt || assessment?.postResume?.continuedAt,
  );
}

export function hasActivePostRecordingResume(assessment, nowMs = Date.now()) {
  if (
    !assessment?.walk ||
    !assessment?.preMeasurementId ||
    hasPostRecordingContinueIntent(assessment) ||
    assessment?.postMeasurementId
  ) {
    return false;
  }

  const expiresAtMs = parseTimestampMs(assessment?.postResume?.expiresAt);
  return Number.isFinite(expiresAtMs) && nowMs <= expiresAtMs;
}

export function getPostRecordingResumeStartAt(assessment) {
  return (
    assessment?.postResume?.timeToRecordingStartedAt ||
    assessment?.returnedAt ||
    assessment?.walk?.returnedAt ||
    assessment?.walk?.trackingStoppedAt ||
    assessment?.walk?.completedAt ||
    null
  );
}

export function resolveResumePath(assessment, nowMs = Date.now()) {
  if (assessment?.postMeasurementId) {
    return `${SIX_MINUTE_WALK_BASE_PATH}/post`;
  }

  if (hasPostRecordingContinueIntent(assessment)) {
    return `${SIX_MINUTE_WALK_BASE_PATH}/post`;
  }

  if (hasActivePostRecordingResume(assessment, nowMs)) {
    return `${SIX_MINUTE_WALK_BASE_PATH}/post`;
  }

  if (assessment?.walk) {
    return `${SIX_MINUTE_WALK_BASE_PATH}/walk`;
  }

  if (assessment?.preMeasurementId) {
    return `${SIX_MINUTE_WALK_BASE_PATH}/walk`;
  }

  return `${SIX_MINUTE_WALK_BASE_PATH}/pre`;
}
